import type { SVGProps } from 'react';

export type IconProps = SVGProps<SVGSVGElement>;

const baseProps = {
  fill: 'none' as const,
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

export function ArrowUpRightIcon({ width = 24, height = 24, ...props }: IconProps) {
  return (
    <svg width={width} height={height} viewBox="0 0 24 24" {...baseProps} {...props}>
      <path d="M7 17L17 7" />
      <path d="M9.5 7H17v7.5" />
    </svg>
  );
}

export function ArrowDownRightIcon({ width = 24, height = 24, ...props }: IconProps) {
  return (
    <svg width={width} height={height} viewBox="0 0 24 24" {...baseProps} {...props}>
      <path d="M7 7l10 10" />
      <path d="M9.5 17H17v-7.5" />
    </svg>
  );
}

export function InfoCircleIcon({ width = 24, height = 24, ...props }: IconProps) {
  return (
    <svg width={width} height={height} viewBox="0 0 24 24" {...baseProps} {...props}>
      <circle cx={12} cy={12} r={9} />
      <line x1={12} y1={10} x2={12} y2={16} />
      <line x1={12} y1={7.5} x2={12.01} y2={7.5} />
    </svg>
  );
}

export function MinusIcon({ width = 24, height = 24, ...props }: IconProps) {
  return (
    <svg width={width} height={height} viewBox="0 0 24 24" {...baseProps} {...props}>
      <line x1={6} y1={12} x2={18} y2={12} />
    </svg>
  );
}

export function PlusIcon({ width = 24, height = 24, ...props }: IconProps) {
  return (
    <svg width={width} height={height} viewBox="0 0 24 24" {...baseProps} {...props}>
      <line x1={12} y1={5} x2={12} y2={19} />
      <line x1={5} y1={12} x2={19} y2={12} />
    </svg>
  );
}

export function SendIcon({ width = 24, height = 24, ...props }: IconProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z" />
      <path d="m21.854 2.147-10.94 10.939" />
    </svg>
  );
}

export function MessageCircleIcon({ width = 24, height = 24, ...props }: IconProps) {
  return (
    <svg width={width} height={height} viewBox="0 0 24 24" {...baseProps} {...props}>
      <path d="M21 11.5a8.5 8.5 0 1 1-3.52-6.81L21 3" />
      <path d="M21 3v5h-5" />
    </svg>
  );
}

export function PhoneIcon({ width = 24, height = 24, ...props }: IconProps) {
  return (
    <svg width={width} height={height} viewBox="0 0 24 24" {...baseProps} {...props}>
      <path d="M5 4h3l2 5-2 2a12 12 0 0 0 6 6l2-2 5 2v3a2 2 0 0 1-2 2 16 16 0 0 1-16-16 2 2 0 0 1 2-2Z" />
    </svg>
  );
}

export function ChartLineIcon({ width = 24, height = 24, ...props }: IconProps) {
  return (
    <svg width={width} height={height} viewBox="0 0 24 24" {...baseProps} {...props}>
      <path d="M4 19h16" />
      <path d="M5 15l4-4 3 3 6-6" />
      <circle cx={18} cy={7} r={0.01} />
    </svg>
  );
}

export function CompareIcon({ width = 24, height = 24, ...props }: IconProps) {
  return (
    <svg width={width} height={height} viewBox="0 0 24 24" {...baseProps} {...props}>
      <path d="M7 3v18" />
      <path d="M17 3v18" />
      <path d="M3 7h8" />
      <path d="M13 17h8" />
    </svg>
  );
}

export function CalculatorIcon({ width = 24, height = 24, ...props }: IconProps) {
  return (
    <svg width={width} height={height} viewBox="0 0 24 24" {...baseProps} {...props}>
      <rect x={4} y={3} width={16} height={18} rx={2} />
      <path d="M8 7h8" />
      <path d="M8 12h0" />
      <path d="M12 12h0" />
      <path d="M16 12h0" />
      <path d="M8 16h0" />
      <path d="M12 16h0" />
      <path d="M16 16h0" />
    </svg>
  );
}

export function HeartHandshakeIcon({ width = 24, height = 24, ...props }: IconProps) {
  return (
    <svg width={width} height={height} viewBox="0 0 24 24" {...baseProps} {...props}>
      <path d="M12 21s-6-4.35-8.28-8.13C1.69 10.23 2.36 6.5 5.1 5.03 7.06 3.98 9.24 4.57 10.5 6.09 11.76 4.57 13.94 3.98 15.9 5.03c2.74 1.47 3.41 5.2 1.38 7.84C18 14.27 16.53 16.16 15 17.7" />
      <path d="M8.5 13.5 10 15a2 2 0 0 0 2.83 0l1.92-1.92a1 1 0 0 0 0-1.41L13.3 10.2" />
      <path d="M15 10.7 12.7 13m-4.2-2.8-1.3 1.3a1 1 0 0 0 0 1.41l1.96 1.96" />
    </svg>
  );
}

export function BitcoinHeartIcon({ width = 24, height = 24, ...props }: IconProps) {
  return (
    <svg width={width} height={height} viewBox="0 0 24 24" {...baseProps} {...props}>
      <circle cx={10} cy={12} r={5.2} />
      <path d="M8.4 8.6v6.8" />
      <path d="M8.4 9.7h2.9a1.4 1.4 0 1 0 0-2.8H8.4" />
      <path d="M8.4 12.2h3.2a1.5 1.5 0 0 1 0 3H8.4" />
      <path d="M15.6 13.4c0-1.1.9-2 2-2 .8 0 1.4.4 1.8.9.4-.5 1-.9 1.8-.9 1.1 0 2 .9 2 2 0 1.5-1.1 2.6-3 4.1l-.8.7-.8-.7c-1.9-1.5-3-2.6-3-4.1Z" />
    </svg>
  );
}

export function PurchaseBagIcon({ width = 24, height = 24, ...props }: IconProps) {
  return (
    <svg width={width} height={height} viewBox="0 0 24 24" {...baseProps} {...props}>
      <path d="M6.5 9h11l1.2 11H5.3z" />
      <path d="M8.5 9V7.5a3.5 3.5 0 0 1 7 0V9" />
      <circle cx={12} cy={14.5} r={2.5} />
      <path d="M11.5 12v5" />
      <path d="M12.5 14.5h1.2" />
    </svg>
  );
}

export function TicketIcon({ width = 20, height = 20, ...props }: IconProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 20 20"
      {...baseProps}
      strokeWidth={1.5}
      {...props}
    >
      <path d="M3 6.25V5.5A1.5 1.5 0 0 1 4.5 4h11A1.5 1.5 0 0 1 17 5.5v.75a1.9 1.9 0 0 0 0 3.8v.75a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 3 11.05V10.3a1.9 1.9 0 0 0 0-3.8Z" />
      <path d="M7.75 7.25h4.5" />
      <path d="M7.75 9.75h2.75" />
    </svg>
  );
}

export function CopyIcon({ width = 24, height = 24, ...props }: IconProps) {
  return (
    <svg width={width} height={height} viewBox="0 0 24 24" {...baseProps} {...props}>
      <rect x={9} y={9} width={12} height={12} rx={2} />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

export function ShareIcon({ width = 24, height = 24, ...props }: IconProps) {
  return (
    <svg width={width} height={height} viewBox="0 0 24 24" {...baseProps} {...props}>
      <circle cx={18} cy={5} r={3} />
      <circle cx={6} cy={12} r={3} />
      <circle cx={18} cy={19} r={3} />
      <path d="M8.59 13.51 15.42 17.5" />
      <path d="M15.41 6.51 8.59 10.49" />
    </svg>
  );
}

export function SparklineIcon({ width = 24, height = 24, ...props }: IconProps) {
  return (
    <svg width={width} height={height} viewBox="0 0 24 24" {...baseProps} {...props}>
      <path d="M3.5 18.5H20.5" />
      <path d="M5.5 14.5l4-6 3 4 3-5 3.5 5.5" />
      <path d="M17.5 6.5 20 5" />
      <path d="M6 6l-2-1" />
    </svg>
  );
}

export function ImageIcon({ width = 24, height = 24, ...props }: IconProps) {
  return (
    <svg width={width} height={height} viewBox="0 0 24 24" {...baseProps} {...props}>
      <rect x={4} y={5} width={16} height={14} rx={2} />
      <path d="M4 15l4-4 3 3 4-4 5 5" />
      <circle cx={9} cy={9} r={1.2} />
    </svg>
  );
}

export function RotateCcwIcon({ width = 24, height = 24, ...props }: IconProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
      <path d="M3 3v5h5" />
    </svg>
  );
}

export function ArchiveBoxIcon({ width = 24, height = 24, ...props }: IconProps) {
  return (
    <svg width={width} height={height} viewBox="0 0 24 24" {...baseProps} {...props}>
      <rect x={3.5} y={6} width={17} height={14} rx={2.5} />
      <path d="M3.5 10h17" />
      <path d="M8.5 4h7l1 2H7.5z" />
      <path d="M10 15h4" />
    </svg>
  );
}

export function BotIcon({ width = 24, height = 24, ...props }: IconProps) {
  return (
    <svg width={width} height={height} viewBox="0 0 24 24" {...baseProps} {...props}>
      <path d="M12 8V4" />
      <path d="M9 4h6" />
      <rect x={5} y={8} width={14} height={10} rx={3} />
      <path d="M8 18v1a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2v-1" />
      <path d="M8 12h.01" />
      <path d="M16 12h.01" />
      <path d="M3 12h2" />
      <path d="M19 12h2" />
    </svg>
  );
}

export function GlobeIcon({ width = 24, height = 24, ...props }: IconProps) {
  return (
    <svg width={width} height={height} viewBox="0 0 24 24" {...baseProps} {...props}>
      <circle cx={12} cy={12} r={9} />
      <path d="M3 12h18" />
      <path d="M12 3a15.3 15.3 0 0 1 4 9 15.3 15.3 0 0 1-4 9 15.3 15.3 0 0 1-4-9 15.3 15.3 0 0 1 4-9Z" />
    </svg>
  );
}

export function LightningBoltIcon({ width = 24, height = 24, ...props }: IconProps) {
  // Filled bolt for better small-size legibility
  return (
    <svg width={width} height={height} viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M13 2 5 14h5l-1 8 8-12h-5z" />
    </svg>
  );
}

export function TrophyIcon({ width = 24, height = 24, ...props }: IconProps) {
  return (
    <svg width={width} height={height} viewBox="0 0 24 24" {...baseProps} {...props}>
      <path d="M8 22h8" />
      <path d="M12 18v4" />
      <path d="M7 4V2h10v2" />
      <path d="M17 4h4v3a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V4h4" />
      <path d="M17 10a5 5 0 0 1-10 0" />
    </svg>
  );
}

export function UsersIcon({ width = 24, height = 24, ...props }: IconProps) {
  return (
    <svg width={width} height={height} viewBox="0 0 24 24" {...baseProps} {...props}>
      <path d="M8 14a5 5 0 1 0-6 0" />
      <path d="M22 14a5 5 0 1 0-6 0" />
      <path d="M13 6a5 5 0 1 0-2 0" />
      <path d="M6 18v3" />
      <path d="M18 18v3" />
      <path d="M12 13v8" />
    </svg>
  );
}

export function WalletIcon({ width = 24, height = 24, ...props }: IconProps) {
  return (
    <svg width={width} height={height} viewBox="0 0 24 24" {...baseProps} {...props}>
      <rect x={3} y={5} width={18} height={14} rx={2.5} />
      <path d="M16 5V3a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v4" />
      <path d="M18 14h.01" />
    </svg>
  );
}
